const SCORE_KEYS = ['overall', 'goalCoverage', 'responseQuality', 'efficiency'];

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function humanize(value = '') {
  return String(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeEvaluator(item, index) {
  const rawScore = item?.evaluationScore ?? item?.score ?? item?.meanScore ?? item?.mean;
  const numericScore = rawScore === null || rawScore === undefined || rawScore === ''
    ? null
    : asNumber(rawScore, null);
  const score = numericScore !== null && numericScore > 1 ? numericScore / 100 : numericScore;
  const report = String(item?.report ?? item?.reportNumber ?? item?.group ?? index + 1);
  const field = item?.field || item?.name || item?.evaluator || `Evaluator ${index + 1}`;

  return {
    id: String(item?.id || `${report}-${field}-${index}`),
    report,
    field,
    evaluator: item?.aiEvaluator || item?.evaluator || item?.metric || field,
    framework: item?.framework || 'Not specified',
    approach: item?.approach || item?.method || 'Not specified',
    score,
    detail: item?.detail || item?.details || null,
    source: item?.source || null,
    band: score === null ? 'not-run' : score >= 0.85 ? 'green' : score >= 0.75 ? 'amber' : 'red',
  };
}

/**
 * Read the project, cycle, and scenario a run belongs to.
 * Top-level scenarioId identifies the evaluated area, not the rows in `results`.
 * @param {Object} payload - Raw evaluation payload.
 * @returns {Object} Placement metadata.
 */
function extractMeta(payload) {
  const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
  return {
    projectExternalId: text(payload.projectId),
    projectName: text(payload.projectName),
    projectDescription: text(payload.projectDescription),
    scenarioExternalId: text(payload.scenarioId),
    scenarioName: text(payload.scenarioName),
    testCycle: text(payload.testCycle),
    runExternalId: text(payload.runId),
  };
}

function parseEvaluatorReport(payload, fileName) {
  const sourceRows = Array.isArray(payload.evaluators)
    ? payload.evaluators
    : Array.isArray(payload.scores)
      ? payload.scores
      : payload.results;
  const evaluators = sourceRows.map(normalizeEvaluator);
  const scored = evaluators.filter((item) => item.score !== null);
  const overallAverage = scored.length
    ? scored.reduce((sum, item) => sum + item.score, 0) / scored.length
    : 0;

  return {
    format: 'evaluator',
    title: payload.title || 'AI Evaluators – Consolidated Scores',
    workspace: payload.workspace || payload.name || 'AI Evaluation',
    description: payload.description || 'Summary rollup of every evaluator in this workspace.',
    fileName,
    meta: extractMeta(payload),
    generatedAt: payload.generatedAt || payload.generated || null,
    sources: Array.isArray(payload.sources) ? payload.sources : [],
    evaluators,
    reports: payload.reports || [],
    summary: {
      overallAverage,
      evaluatorsScored: scored.length,
      notRun: evaluators.length - scored.length,
      belowTarget: scored.filter((item) => item.score < 0.75).length,
      totalEvaluators: evaluators.length,
    },
  };
}

function normalizeScenario(result, index) {
  const scores = Object.fromEntries(
    SCORE_KEYS.map((key) => [key, asNumber(result?.scores?.[key])]),
  );
  const coveredFacts = Array.isArray(result?.coveredFacts) ? result.coveredFacts : [];
  const missingFacts = Array.isArray(result?.missingFacts) ? result.missingFacts : [];
  const allFacts = [...coveredFacts, ...missingFacts];

  return {
    ...result,
    scenarioId: String(result?.scenarioId ?? index + 1),
    category: result?.category || `scenario-${index + 1}`,
    categoryLabel: humanize(result?.category || `Scenario ${index + 1}`),
    purpose: result?.purpose || 'No purpose was provided.',
    endGoal: result?.endGoal || '',
    evaluationMode: result?.evaluationMode || 'unknown',
    passed: Boolean(result?.passed),
    scores,
    coveredFacts,
    missingFacts,
    allFacts,
    factCount: allFacts.length,
    coveredFactCount: coveredFacts.filter((fact) => fact?.covered !== false).length,
    aiSummary: result?.aiEvaluation?.summary || '',
    provider: result?.aiEvaluation?.provider || '',
    model: result?.aiEvaluation?.model || '',
    totalTokens: asNumber(result?.aiEvaluation?.usage?.totalTokens),
    turns: asNumber(result?.turns),
    responses: Array.isArray(result?.responses) ? result.responses : [],
  };
}

export function parseEvaluationResults(input, fileName = 'evaluation-results.json') {
  let payload = input;
  if (typeof input === 'string') {
    try {
      payload = JSON.parse(input);
    } catch {
      throw new Error('The selected file is not valid JSON.');
    }
  }

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('The evaluation file must contain a JSON object.');
  }
  const evaluatorRows = payload.evaluators || payload.scores;
  const resultRows = payload.results;
  const looksLikeEvaluatorReport = Array.isArray(evaluatorRows)
    || (Array.isArray(resultRows) && resultRows.some((item) => (
      'evaluationScore' in item || 'framework' in item || 'approach' in item || 'field' in item
    )));

  if (looksLikeEvaluatorReport) {
    return parseEvaluatorReport(payload, fileName);
  }
  if (!Array.isArray(resultRows)) {
    throw new Error('The evaluation file is missing a results, evaluators, or scores array.');
  }

  const scenarios = payload.results.map(normalizeScenario);
  const passed = scenarios.filter((scenario) => scenario.passed).length;
  const failed = scenarios.length - passed;
  const average = (key) => scenarios.length
    ? scenarios.reduce((sum, scenario) => sum + scenario.scores[key], 0) / scenarios.length
    : 0;

  return {
    format: 'scenario',
    fileName,
    meta: extractMeta(payload),
    generatedAt: payload.generatedAt || null,
    schemaVersion: String(payload.schemaVersion || 'unknown'),
    status: payload.status || 'unknown',
    error: payload.error || null,
    rubric: payload.rubric || {},
    scenarios,
    summary: {
      configuredScenarios: asNumber(payload.summary?.configuredScenarios, scenarios.length),
      completedScenarios: asNumber(payload.summary?.completedScenarios, scenarios.length),
      passed,
      failed,
      passRate: scenarios.length ? (passed / scenarios.length) * 100 : 0,
      averageOverall: average('overall'),
      averageGoalCoverage: average('goalCoverage'),
      averageQuality: average('responseQuality'),
      averageEfficiency: average('efficiency'),
      totalTokens: scenarios.reduce((sum, scenario) => sum + scenario.totalTokens, 0),
    },
  };
}

export { humanize };